import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import logoUrl from "@/assets/powercom-logo.png";
import logoWhite from "@/assets/powercom-logo-white.png";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      navigate(user.role === "employee" ? "/dotazioni" : "/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left visual panel */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg"
          alt="Magazzino"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/85" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <img src={logoWhite} alt="POWERCOM" data-testid="login-brand-logo" className="h-9 w-auto" />
          </div>
          <div>
            <h1 className="font-heading font-black text-4xl xl:text-5xl tracking-tighter leading-[1.05]">
              Gestione asset<br />aziendali
            </h1>
            <p className="mt-4 text-base opacity-90 max-w-md">
              Censimento mezzi e attrezzature, manutenzioni, assegnazioni con firma
              digitale e documenti sempre a portata di mano.
            </p>
          </div>
          <div className="label-caps text-[0.65rem] opacity-70">POWERCOM S.R.L. — P.IVA 10280401216</div>
        </div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <img src={logoUrl} alt="POWERCOM" data-testid="login-brand-logo-mobile" className="h-8 w-auto" />
          </div>
          <div className="label-caps text-muted-foreground mb-2">Accesso riservato</div>
          <h2 className="font-heading font-black text-3xl tracking-tight mb-1">Bentornato</h2>
          <p className="text-muted-foreground mb-8 text-sm">Accedi al gestionale asset.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@powercom.srl"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 text-base font-semibold">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Accedi"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
